import React, { useState } from 'react';
import { MapPin, Phone, User, Building2, CheckCircle2, HelpCircle } from 'lucide-react';
import type { RoutyContactPickerArtifact, ContactPickChoice } from '../../business/routy/routyArtifacts';

interface ContactPickerCardProps {
  artifact: RoutyContactPickerArtifact;
  disabled?: boolean;
  onPick: (artifact: RoutyContactPickerArtifact, candidate: ContactPickChoice) => void;
  onNone: (artifact: RoutyContactPickerArtifact) => void;
}

const ContactPickerCard: React.FC<ContactPickerCardProps> = ({ artifact, disabled, onPick, onNone }) => {
  const [resolved, setResolved] = useState(false);
  const locked = disabled || resolved;

  if (artifact.candidates.length === 0) return null;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <HelpCircle className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">{artifact.prompt || 'Which one did you mean?'}</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.candidates.map((cand) => (
          <div key={cand.contactId} className="rounded-lg border border-ink-200 bg-surface p-3">
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-ink-900 truncate">{cand.name}</span>
            </div>
            {cand.businessName && (
              <div className="mt-1 flex items-center gap-2 pl-5 text-xs text-ink-500">
                <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-ink-400" />
                <span className="truncate">{cand.businessName}</span>
              </div>
            )}
            {cand.phone && (
              <div className="mt-1 flex items-center gap-2 pl-5 text-xs text-ink-500">
                <Phone className="w-3.5 h-3.5 flex-shrink-0 text-ink-400" />
                <span>{cand.phone}</span>
              </div>
            )}
            {cand.address && (
              <div className="mt-1 flex items-start gap-2 pl-5 text-xs text-ink-500">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-400" />
                <span className="truncate">{cand.address}</span>
              </div>
            )}
            <button
              onClick={() => {
                setResolved(true);
                onPick(artifact, cand);
              }}
              disabled={locked}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              This one
            </button>
          </div>
        ))}

        {artifact.allowNone && (
          <button
            onClick={() => {
              setResolved(true);
              onNone(artifact);
            }}
            disabled={locked}
            className="w-full py-2 rounded-lg border border-ink-200 text-xs font-medium text-ink-600 hover:bg-ink-50 transition-colors disabled:opacity-50"
          >
            None of these
          </button>
        )}
      </div>
    </div>
  );
};

export default ContactPickerCard;
